import type { IconName } from '~/content/types';

/**
 * Categories shown in the /help (Learn) sidebar. Each `label` is used both as
 * the filter value and as the tag rendered on matching articles, so the
 * `category` on every {@link LearnArticle} must match one of these labels
 * exactly (see `help.tsx` filter: `article.category === label`).
 */
export interface LearnCategory {
  label: string;
  icon: IconName;
}

export const learnCategories: LearnCategory[] = [
  { label: 'Getting started', icon: 'user' },
  { label: 'Accounts', icon: 'bank' },
  { label: 'Cards', icon: 'card' },
  { label: 'Payments & transfers', icon: 'swap' },
  { label: 'Rewards', icon: 'percent' },
  { label: 'Security', icon: 'shield' },
];

/** Outline depth for article headings (h2–h4). Used by the detail TOC inset. */
export type ArticleHeadingLevel = 2 | 3 | 4;

/**
 * A single ordered block within an article body. Modeled on the privacy
 * statement's `PrivacyBlock` union (see `privacy.ts`): a `heading` renders as an
 * <h2>–<h4> section title (and becomes a quick-nav anchor on the detail page),
 * while `paragraph` / `list` / `image` carry the running copy and media.
 */
export type ArticleBlock =
  | { type: 'heading'; text: string; level?: ArticleHeadingLevel }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'image'; src: string; alt?: string; caption?: string }
  | {
      type: 'table';
      columns: string[];
      rows: string[][];
      /** Emphasize the final row (e.g. Webflow `row-total` budget totals). */
      highlightLastRow?: boolean;
    }
  | {
      type: 'promoCard';
      eyebrow?: string;
      title: string;
      body: string;
      tone: 'red' | 'dark' | 'light';
      ctaLabel: string;
      ctaHref: string;
      openInNewTab?: boolean;
    }
  | {
      type: 'branchDirectory';
      title: string;
      searchPlaceholder: string;
      filterLabel: string;
      emptyMessage: string;
      entries: Array<{
        branchName: string;
        district: string;
        routingNumber: string;
        address?: string;
      }>;
    }
  | {
      type: 'chart';
      eyebrow?: string;
      title: string;
      subtitle?: string;
      chartType: 'bar' | 'line' | 'doughnut';
      horizontal?: boolean;
      valuePrefix?: string;
      valueSuffix?: string;
      source?: string;
      labels: string[];
      datasets: Array<{
        label?: string;
        color?: string;
        values: number[];
        colors?: string[];
        displayValues?: string[];
      }>;
      highlightedLabel?: string;
    }
  | {
      type: 'statCards';
      eyebrow?: string;
      title?: string;
      subtitle?: string;
      items: Array<{ eyebrow?: string; value: string; label: string }>;
      source?: string;
    }
  | {
      type: 'video';
      src: string;
      title: string;
      caption?: string;
    };

/** The person credited with writing an article, shown in the detail byline. */
export interface LearnAuthor {
  name: string;
  /** Portrait URL rendered as the 48×48 byline avatar. */
  avatar: string;
}

export interface LearnArticle {
  title: string;
  /** Pre-formatted display date, e.g. "Jan 13, 2026". */
  date: string;
  /** Must match a {@link LearnCategory} label so sidebar filtering works. */
  category: string;
  excerpt: string;
  /** URL-safe id within the regional collection (e.g. `buy-spacex-spcx-shares`). */
  slug: string;
  /** Webflow collection slug used as the URL prefix (e.g. `article`, `bangladesh`). */
  urlPrefix: string;
  /** Canonical path without leading slash (e.g. `bangladesh/buy-spacex-spcx-shares`). */
  fullPath: string;
  /** Structured article copy rendered on the detail page. */
  body: ArticleBlock[];
  /** Byline author shown on the detail page (name + avatar). */
  author: LearnAuthor;
  /** SEO `<title>`; falls back to article title when empty. */
  metaTitle?: string;
  metaDescription?: string;
  ogImageUrl?: string;
  thumbnailUrl?: string;
}

/** Public URL for an article detail page (preserves legacy Webflow paths). */
export function learnArticleHref(article: LearnArticle): string {
  return `/${article.fullPath}`;
}

const fallbackAuthor: LearnAuthor = {
  name: 'nsave team',
  avatar: '/images/shared/profile.jpg',
};

/** The authored fields for an article; `slug` is derived below. */
type LearnArticleSeed = Pick<LearnArticle, 'title' | 'date' | 'category' | 'excerpt'> & {
  /** Optional rich fixture body used to exercise article blocks in local development. */
  body?: ArticleBlock[];
};

/** Turn a title into a URL-safe slug ("How to open…" → "how-to-open…"). */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[’'"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Use only authored seed copy until the full Webflow body has been imported into Payload. */
function makeArticleBody({ excerpt, body }: LearnArticleSeed): ArticleBlock[] {
  return body ?? [{ type: 'paragraph', text: excerpt }];
}

const learnArticleSeeds: LearnArticleSeed[] = [
  // Getting started
  {
    title: 'How to open your nsave account in minutes',
    date: 'Jan 13, 2026',
    category: 'Getting started',
    excerpt: 'A quick walkthrough of sign-up, verification and funding your first account.',
    body: [
      {
        type: 'paragraph',
        text: 'Opening an nsave account takes only a few minutes. Have your identity document ready and follow the steps below.',
      },
      { type: 'heading', text: 'Before you begin', level: 2 },
      {
        type: 'list',
        items: [
          'Use an email address and phone number you can access.',
          'Have a valid passport or supported national identity document ready.',
          'Find a well-lit space for the identity check.',
        ],
      },
      { type: 'heading', text: 'Supported documents', level: 3 },
      {
        type: 'paragraph',
        text: 'The exact documents available depend on your country. The app shows the current options before verification begins.',
      },
      { type: 'heading', text: 'Photo tips', level: 4 },
      {
        type: 'list',
        items: [
          'Place the document on a flat surface with all four corners visible.',
          'Avoid glare, shadows and cropped edges.',
          'Use the original document rather than a scan or screenshot.',
        ],
      },
      { type: 'heading', text: 'Create your account', level: 2 },
      {
        type: 'paragraph',
        text: 'Download the app, enter your details and complete identity verification. Once approved, your account details appear in the app.',
      },
      { type: 'heading', text: 'Download the nsave app', level: 3 },
      {
        type: 'paragraph',
        text: 'Start by downloading the current version of the nsave app from your device’s official app store. Installing the latest version ensures that the screens and verification steps in this walkthrough match what you see.',
      },
      { type: 'heading', text: 'Choose your sign-up details', level: 4 },
      {
        type: 'paragraph',
        text: 'Use an email address and phone number that belong to you and that you expect to keep. These details help you sign in, receive important account messages and recover access if you change devices later.',
      },
      { type: 'heading', text: 'Confirm your contact information', level: 4 },
      {
        type: 'paragraph',
        text: 'Enter each verification code as soon as it arrives. If a code expires, request a new one and check that your device has a stable connection before trying again.',
      },
      { type: 'heading', text: 'Tell us about yourself', level: 3 },
      {
        type: 'paragraph',
        text: 'The app asks for a few personal details so it can create the correct account profile. Take a moment to review every field before continuing, particularly your full legal name and date of birth.',
      },
      { type: 'heading', text: 'Match your identity document', level: 4 },
      {
        type: 'paragraph',
        text: 'Enter your name exactly as it appears on the document you plan to use. Include every given name, preserve the same spelling and avoid nicknames or shortened versions.',
      },
      { type: 'heading', text: 'Add your residential address', level: 4 },
      {
        type: 'paragraph',
        text: 'Use the address where you currently live. If the app asks for supporting information, follow the on-screen guidance and make sure any uploaded document is recent and fully readable.',
      },
      { type: 'heading', text: 'Verify your identity', level: 2 },
      {
        type: 'paragraph',
        text: 'Identity verification is an important part of opening a financial account. Set aside a few uninterrupted minutes, clean your camera lens and keep your original document nearby before starting.',
      },
      { type: 'heading', text: 'Scan your document', level: 3 },
      {
        type: 'paragraph',
        text: 'Follow the frame shown on screen and hold your device steady while the image is captured. Check that the photo, document number and machine-readable area are sharp before submitting it.',
      },
      { type: 'heading', text: 'Complete the face check', level: 3 },
      {
        type: 'paragraph',
        text: 'Stand in even light with your whole face visible and remove anything that creates heavy shadows. Follow the movement prompts slowly and keep looking at the screen until the check finishes.',
      },
      { type: 'heading', text: 'If a check needs another try', level: 4 },
      {
        type: 'paragraph',
        text: 'A retry usually means the camera could not read part of the document or face scan clearly. Move away from glare, use a plain background and repeat the capture with the document lying flat.',
      },
      { type: 'heading', text: 'Review your application', level: 2 },
      {
        type: 'paragraph',
        text: 'Once all required details have been submitted, the app shows that your application is being reviewed. You can close the app and return later without restarting the completed steps.',
      },
      { type: 'heading', text: 'How the review works', level: 3 },
      {
        type: 'paragraph',
        text: 'Some applications can be checked automatically, while others need a manual review. Keep notifications enabled so you see any update or request for clarification as soon as it is available.',
      },
      { type: 'heading', text: 'When more information is requested', level: 3 },
      {
        type: 'paragraph',
        text: 'Open the request in the app and read the full explanation before uploading anything. Respond with the exact information requested rather than sending several unrelated documents.',
      },
      { type: 'heading', text: 'Explore your new account', level: 2 },
      {
        type: 'paragraph',
        text: 'After approval, spend a few minutes exploring the account home screen. This is where you can see available balances, open account details and find the actions supported for each currency.',
      },
      { type: 'heading', text: 'Find your account details', level: 3 },
      {
        type: 'paragraph',
        text: 'Select a balance to view the receiving details available to you. Labels and transfer methods can differ by currency, so use the details shown for the specific payment you are expecting.',
      },
      { type: 'heading', text: 'Copy details safely', level: 4 },
      {
        type: 'paragraph',
        text: 'Use the copy or share controls in the app instead of typing long account numbers manually. Before sending the details, confirm both the currency and the name of the intended recipient.',
      },
      { type: 'heading', text: 'Add money for the first time', level: 2 },
      {
        type: 'paragraph',
        text: 'Choose the balance you want to fund and review its available receiving methods. Start with an amount you are comfortable testing and keep the sending bank’s confirmation until the transfer arrives.',
      },
      { type: 'heading', text: 'Prepare your first bank transfer', level: 3 },
      {
        type: 'paragraph',
        text: 'Copy the beneficiary and bank information exactly as displayed. If a reference is provided, include it in the correct reference field so the incoming payment can be identified.',
      },
      { type: 'heading', text: 'Check the currency before sending', level: 4 },
      {
        type: 'paragraph',
        text: 'The sending currency should match the receiving details you selected. A bank may offer its own conversion, so review the final currency and amount on the bank’s confirmation screen.',
      },
      { type: 'heading', text: 'Track the incoming payment', level: 4 },
      {
        type: 'paragraph',
        text: 'Return to the activity view to look for the payment. Transfer timing depends on the method, sending institution and processing schedule, so keep the receipt if you need to investigate a delay.',
      },
      { type: 'heading', text: 'Secure your account', level: 2 },
      {
        type: 'paragraph',
        text: 'A few security checks now can make everyday use easier later. Review your device settings, protect access to your email and never share a sign-in or verification code with another person.',
      },
      { type: 'heading', text: 'Set up biometric access', level: 3 },
      {
        type: 'paragraph',
        text: 'If your device supports it, biometric access can reduce the need to enter your passcode in public. Only enable it on a personal device with a secure screen lock.',
      },
      { type: 'heading', text: 'Review notification settings', level: 3 },
      {
        type: 'paragraph',
        text: 'Allow important account and transaction notifications. Prompt alerts make it easier to notice a completed transfer, a pending request or activity you do not recognize.',
      },
      { type: 'heading', text: 'Plan for account recovery', level: 4 },
      {
        type: 'paragraph',
        text: 'Keep your registered contact details current and maintain secure access to them. If you replace your phone, follow the in-app recovery flow rather than creating a second account.',
      },
      { type: 'heading', text: 'Common sign-up questions', level: 2 },
      {
        type: 'paragraph',
        text: 'Most sign-up issues can be resolved by checking the information already entered and following the latest prompt in the app. The sections below provide a few useful places to start.',
      },
      { type: 'heading', text: 'Why does my name need to match?', level: 3 },
      {
        type: 'paragraph',
        text: 'Consistent information helps connect your application to your identity document. If your legal name has changed, use the current document and provide supporting information when the app requests it.',
      },
      { type: 'heading', text: 'Can I finish the application later?', level: 3 },
      {
        type: 'paragraph',
        text: 'Completed steps are normally saved, but time-sensitive checks may need to be repeated. Return using the same email address and phone number, then continue from the screen shown.',
      },
      { type: 'heading', text: 'What if I change phones?', level: 3 },
      {
        type: 'paragraph',
        text: 'Install the app on the new device and sign in with your existing account details. Extra verification may be required because the device is new, so keep your identity document and registered contact methods available.',
      },
      { type: 'heading', text: 'Your final opening checklist', level: 2 },
      {
        type: 'list',
        items: [
          'Your legal name and personal details match your identity document.',
          'Your email address and phone number are verified and accessible.',
          'Your identity photos are clear and every requested step is submitted.',
          'You can find the correct receiving details for each available currency.',
          'Security notifications and device protection are enabled.',
        ],
      },
      {
        type: 'image',
        src: '/images/accounts/hero-960.webp',
        alt: 'Friends using nsave together',
        caption: 'Landscape image fixture with a short editorial caption.',
      },
      {
        type: 'promoCard',
        eyebrow: 'Local development example',
        title: 'Ready to get started?',
        body: 'Open your nsave account and get your global account details in one place.',
        tone: 'red',
        ctaLabel: 'Open an nsave Account',
        ctaHref: '/get-the-app',
      },
    ],
  },
  {
    title: 'Verifying your identity: what you’ll need',
    date: 'Jan 6, 2026',
    category: 'Getting started',
    excerpt: 'The documents and details that help us approve your account faster.',
    body: [
      {
        type: 'paragraph',
        text: 'Identity verification protects your account and helps nsave meet financial regulations. This local fixture exercises a compact table, portrait media and a dark promotional card.',
      },
      { type: 'heading', text: 'Documents we may ask for', level: 2 },
      {
        type: 'table',
        columns: ['Document', 'Example', 'Check before uploading'],
        rows: [
          ['Identity', 'Passport or national identity card', 'Current and undamaged'],
          ['Address', 'Bank statement or utility bill', 'Name and address are visible'],
          ['Tax details', 'Local tax identification number', 'Matches your application'],
        ],
      },
      { type: 'heading', text: 'Taking a clear verification photo', level: 2 },
      {
        type: 'image',
        src: '/images/shared/testimonial-remote-workers.jpg',
        alt: 'Person holding a phone in a well-lit room',
        caption:
          'Portrait image fixture: this should be constrained and centred in the reading column.',
      },
      { type: 'heading', text: 'Common reasons a check needs another try', level: 3 },
      {
        type: 'list',
        items: [
          'The image is blurred or part of the document is outside the frame.',
          'The document has expired or the details do not match the application.',
          'Lighting hides security features or makes the text difficult to read.',
        ],
      },
      {
        type: 'promoCard',
        eyebrow: 'Need more help?',
        title: 'Review the full verification guide',
        body: 'This dark card also tests an external call to action that opens in a new tab.',
        tone: 'dark',
        ctaLabel: 'Visit the nsave website',
        ctaHref: 'https://website.nsave.com/help',
        openInNewTab: true,
      },
    ],
  },
  {
    title: 'Setting up the nsave app on a new phone',
    date: 'Dec 30, 2025',
    category: 'Getting started',
    excerpt: 'Move to a new device and sign back in securely in a couple of minutes.',
    body: [
      {
        type: 'paragraph',
        text: 'You can move your nsave account to a replacement phone without changing your account details or balances.',
      },
      { type: 'heading', text: 'Before changing devices', level: 2 },
      {
        type: 'list',
        items: [
          'Keep your existing phone signed in until the new device is ready.',
          'Confirm that you can receive messages on your registered number.',
          'Install the latest version of the nsave app.',
        ],
      },
      { type: 'heading', text: 'Sign in on the new phone', level: 2 },
      {
        type: 'image',
        src: '/images/compare/download-app.jpg',
        alt: 'The nsave app shown on a phone',
        caption: 'Near-square media fixture at the full width of the article column.',
      },
      { type: 'heading', text: 'If you cannot use the old phone', level: 3 },
      {
        type: 'paragraph',
        text: 'Choose the recovery option during sign-in. For security, support may ask you to repeat part of the identity check before the new device becomes trusted.',
      },
      {
        type: 'promoCard',
        eyebrow: 'Security reminder',
        title: 'Only approve devices you recognise',
        body: 'You can review active sessions and remove an old phone after the new one is working.',
        tone: 'light',
        ctaLabel: 'Read device security tips',
        ctaHref: '/article/managing-trusted-devices-and-sessions',
      },
    ],
  },
  {
    title: 'A tour of your nsave dashboard',
    date: 'Dec 22, 2025',
    category: 'Getting started',
    excerpt: 'Get to know balances, transactions and quick actions at a glance.',
    body: [
      {
        type: 'paragraph',
        text: 'Your dashboard brings balances, recent activity and common account actions into one view. The figures below are deliberately fake local UI fixtures.',
      },
      { type: 'heading', text: 'Balance overview examples', level: 2 },
      {
        type: 'statCards',
        eyebrow: 'Demo data',
        title: 'Account snapshot',
        subtitle: 'A mixed set of short and long labels for responsive layout testing.',
        items: [
          { eyebrow: 'Available', value: '$6,523.00', label: 'Total balance across accounts' },
          { eyebrow: 'This month', value: '+$840.25', label: 'Money received' },
          { eyebrow: 'Pending', value: '3', label: 'Transfers awaiting completion' },
          { value: '12.4%', label: 'Change compared with the previous example period' },
        ],
        source: 'Local development fixture — values are not real customer data.',
      },
      { type: 'heading', text: 'Monthly activity', level: 2 },
      {
        type: 'chart',
        eyebrow: 'Demo data',
        title: 'Money in and money out',
        subtitle: 'Grouped vertical bars test multiple datasets and compact month labels.',
        chartType: 'bar',
        valuePrefix: '$',
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'Money in',
            color: '#9B1A1A',
            values: [1200, 1480, 1320, 1810, 1650, 2040],
          },
          {
            label: 'Money out',
            color: '#D6B1AE',
            values: [760, 920, 1100, 980, 1240, 1190],
          },
        ],
        highlightedLabel: 'Jun',
        source: 'Local development fixture.',
      },
    ],
  },
  {
    title: 'Choosing the right account for your needs',
    date: 'Dec 12, 2025',
    category: 'Getting started',
    excerpt: 'Compare account types and pick the setup that fits how you save and spend.',
  },
  {
    title: 'Funding your account for the first time',
    date: 'Dec 2, 2025',
    category: 'Getting started',
    excerpt: 'The quickest ways to add your first deposit and start using nsave.',
  },
  {
    title: 'Getting help and contacting support',
    date: 'Nov 20, 2025',
    category: 'Getting started',
    excerpt: 'Where to find answers and how to reach our team when you need a hand.',
  },

  // Accounts
  {
    title: 'Understanding your USD account and IBAN',
    date: 'Jan 9, 2026',
    category: 'Accounts',
    excerpt: 'Where to find your account details and how to share them to get paid.',
  },
  {
    title: 'Holding multiple currencies in one place',
    date: 'Dec 28, 2025',
    category: 'Accounts',
    excerpt: 'Manage USD, GBP and EUR balances and switch between them instantly.',
    body: [
      {
        type: 'paragraph',
        text: 'Separate balances make it easier to receive, hold and spend in the currencies you use most often.',
      },
      { type: 'heading', text: 'Example balance mix', level: 2 },
      {
        type: 'chart',
        eyebrow: 'Demo portfolio',
        title: 'Share of total balance by currency',
        subtitle: 'The doughnut fixture tests per-segment colours and authored display values.',
        chartType: 'doughnut',
        valueSuffix: '%',
        labels: ['USD', 'EUR', 'GBP', 'Other'],
        datasets: [
          {
            values: [52, 24, 18, 6],
            colors: ['#9B1A1A', '#C9524A', '#E8A09D', '#6B7280'],
            displayValues: ['52%', '24%', '18%', '6%'],
          },
        ],
        highlightedLabel: 'USD',
        source: 'Local development fixture — percentages are illustrative.',
      },
      { type: 'heading', text: 'Converting a balance', level: 2 },
      {
        type: 'paragraph',
        text: 'Choose the balance you want to convert, review the rate and fee, and confirm. The converted amount appears in the destination balance.',
      },
    ],
  },
  {
    title: 'How to find and share your account details',
    date: 'Dec 16, 2025',
    category: 'Accounts',
    excerpt: 'Share the right details so clients and family can send you money.',
    body: [
      {
        type: 'paragraph',
        text: 'Choose the account and currency you want to receive, then copy the displayed details exactly as shown.',
      },
      { type: 'heading', text: 'Example branch directory', level: 2 },
      {
        type: 'paragraph',
        text: 'This development-only directory demonstrates searchable routing-number content authored as a Payload block.',
      },
      {
        type: 'branchDirectory',
        title: 'Example branch routing directory',
        searchPlaceholder: 'Search branch name or routing number',
        filterLabel: 'Filter by district',
        emptyMessage: 'No matching example branches found.',
        entries: [
          {
            branchName: 'Central Example Branch',
            district: 'Dhaka',
            routingNumber: '000000001',
            address: '1 Example Road',
          },
          {
            branchName: 'North Example Branch',
            district: 'Dhaka',
            routingNumber: '000000002',
          },
          {
            branchName: 'Port Example Branch',
            district: 'Chattogram',
            routingNumber: '000000003',
          },
          {
            branchName: 'Sylhet Example Branch',
            district: 'Sylhet',
            routingNumber: '000000004',
          },
          {
            branchName: 'Agrabad Commercial Branch with an intentionally long name',
            district: 'Chattogram',
            routingNumber: '000000005',
            address: '125 Sheikh Mujib Road, Agrabad Commercial Area, Chattogram',
          },
          {
            branchName: 'Rajshahi Example Branch',
            district: 'Rajshahi',
            routingNumber: '000000006',
            address: '42 Test Avenue',
          },
        ],
      },
      { type: 'heading', text: 'Using a routing number safely', level: 3 },
      {
        type: 'paragraph',
        text: 'Confirm the branch and routing number with the recipient before sending a transfer. A routing number identifies a bank branch; it does not identify an individual account.',
      },
    ],
  },
  {
    title: 'Converting between currencies in the app',
    date: 'Dec 5, 2025',
    category: 'Accounts',
    excerpt: 'Exchange between your balances at the real rate with one transparent fee.',
  },
  {
    title: 'Understanding statements and balances',
    date: 'Nov 24, 2025',
    category: 'Accounts',
    excerpt: 'Read your statement, download records and reconcile your balance.',
  },
  {
    title: 'Managing your account limits',
    date: 'Nov 12, 2025',
    category: 'Accounts',
    excerpt: 'See your current limits and learn how to request a higher threshold.',
  },
  {
    title: 'Closing or pausing an account',
    date: 'Oct 30, 2025',
    category: 'Accounts',
    excerpt: 'What happens to your funds and details if you pause or close an account.',
  },

  // Cards
  {
    title: 'Getting and activating your nsave card',
    date: 'Jan 11, 2026',
    category: 'Cards',
    excerpt: 'From ordering your card to your first tap-to-pay in a few simple steps.',
    body: [
      {
        type: 'paragraph',
        text: 'Order your card from the app, follow its delivery progress and activate it only after it arrives.',
      },
      { type: 'heading', text: 'Order and track your card', level: 2 },
      {
        type: 'image',
        src: '/images/cards/hero.webp',
        alt: 'An nsave payment card',
        caption: 'Wide card artwork used to check image spacing against nearby headings and lists.',
      },
      { type: 'heading', text: 'Activate it in the app', level: 2 },
      {
        type: 'list',
        items: [
          'Open Cards and choose the card that has arrived.',
          'Check that the final four digits match the physical card.',
          'Choose Activate card and set your PIN.',
        ],
      },
      {
        type: 'promoCard',
        title: 'Your card controls travel with you',
        body: 'Freeze the card, change limits and review transactions from the app at any time.',
        tone: 'light',
        ctaLabel: 'Explore card controls',
        ctaHref: '/article/freezing-and-managing-your-card-securely',
      },
    ],
  },
  {
    title: 'Freezing and managing your card securely',
    date: 'Dec 31, 2025',
    category: 'Cards',
    excerpt: 'Lock your card, set limits and control spending straight from the app.',
  },
  {
    title: 'Adding your card to Apple Pay and Google Pay',
    date: 'Dec 19, 2025',
    category: 'Cards',
    excerpt: 'Set up mobile payments so you can pay with your phone or watch.',
  },
  {
    title: 'Using your card abroad without hidden fees',
    date: 'Dec 8, 2025',
    category: 'Cards',
    excerpt: 'Spend in local currency at the real rate wherever you travel.',
  },
  {
    title: 'Replacing a lost or stolen card',
    date: 'Nov 26, 2025',
    category: 'Cards',
    excerpt: 'Freeze it instantly and order a replacement in just a few taps.',
  },
  {
    title: 'Setting spending limits and category controls',
    date: 'Nov 15, 2025',
    category: 'Cards',
    excerpt: 'Fine-tune where and how much your card can be used.',
  },
  {
    title: 'Virtual cards for safer online shopping',
    date: 'Nov 4, 2025',
    category: 'Cards',
    excerpt: 'Create single-use and virtual cards to protect your details online.',
  },

  // Payments & transfers
  {
    title: 'Sending money home at the real exchange rate',
    date: 'Jan 8, 2026',
    category: 'Payments & transfers',
    excerpt: 'How international transfers work and why you get the mid-market rate.',
    body: [
      {
        type: 'paragraph',
        text: 'Compare the quoted amount, fee and estimated arrival time before sending. The example below exists only to test long chart labels and custom display values.',
      },
      { type: 'heading', text: 'Example delivery times by route', level: 2 },
      {
        type: 'chart',
        eyebrow: 'Illustrative comparison',
        title: 'Typical transfer time',
        subtitle: 'Horizontal bars keep route names readable in a narrow article column.',
        chartType: 'bar',
        horizontal: true,
        valueSuffix: ' hours',
        labels: [
          'nsave to nsave',
          'Local bank transfer',
          'International bank wire',
          'A deliberately long example route',
        ],
        datasets: [
          {
            label: 'Typical time',
            values: [0.05, 2, 36, 18],
            displayValues: ['Instant', 'About 2 hours', '1–3 business days', 'About 18 hours'],
          },
        ],
        highlightedLabel: 'nsave to nsave',
        source: 'Local development fixture — not a delivery-time commitment.',
      },
      { type: 'heading', text: 'Before you confirm', level: 3 },
      {
        type: 'list',
        items: [
          'Check the recipient name and account details.',
          'Review the amount the recipient is expected to receive.',
          'Save the transfer receipt after confirmation.',
        ],
      },
    ],
  },
  {
    title: 'Adding money to your nsave account',
    date: 'Dec 27, 2025',
    category: 'Payments & transfers',
    excerpt: 'The fastest ways to top up, from bank transfers to card deposits.',
  },
  {
    title: 'Scheduling and tracking a transfer',
    date: 'Dec 15, 2025',
    category: 'Payments & transfers',
    excerpt: 'Send now or later and follow your transfer every step of the way.',
  },
  {
    title: 'Supported countries and currencies',
    date: 'Dec 4, 2025',
    category: 'Payments & transfers',
    excerpt: 'Check where you can send money and which currencies we support.',
  },
  {
    title: 'Understanding transfer fees and timing',
    date: 'Nov 22, 2025',
    category: 'Payments & transfers',
    excerpt: 'Know exactly what you pay and when your money will arrive.',
    body: [
      {
        type: 'paragraph',
        text: 'Transfer timing depends on the destination, currency and receiving network. Review the quoted fee and delivery estimate before confirming.',
      },
      { type: 'heading', text: 'Example transfer comparison', level: 2 },
      {
        type: 'table',
        columns: ['Transfer type', 'Typical delivery', 'Example fee'],
        rows: [
          ['Everyday transfers'],
          ['Internal transfer', 'Instant', 'Free'],
          ['Local bank transfer', 'Minutes to one business day', 'Shown before confirmation'],
          ['International routes'],
          ['International wire', 'One to three business days', 'Varies by route'],
          ['Example monthly total', 'Four transfers', '$12.00'],
        ],
        highlightLastRow: true,
      },
      { type: 'heading', text: 'What each transfer status means', level: 3 },
      {
        type: 'list',
        items: [
          'Pending: nsave has received the instruction and is completing its checks.',
          'Processing: the transfer is moving through the payment network.',
          'Completed: the receiving institution has accepted the transfer.',
        ],
      },
      { type: 'heading', text: 'When a transfer takes longer', level: 4 },
      {
        type: 'paragraph',
        text: 'Weekends, public holidays, recipient details and extra compliance checks can all affect an estimate. The in-app tracker shows the latest available status.',
      },
    ],
  },
  {
    title: 'Setting up recurring payments',
    date: 'Nov 10, 2025',
    category: 'Payments & transfers',
    excerpt: 'Automate regular transfers to family, savings or bills.',
  },
  {
    title: 'What to do if a transfer is delayed',
    date: 'Oct 29, 2025',
    category: 'Payments & transfers',
    excerpt: 'Common reasons transfers take longer and how to resolve them.',
  },

  // Rewards
  {
    title: 'How nsave rewards work',
    date: 'Jan 7, 2026',
    category: 'Rewards',
    excerpt: 'Earn on your everyday balance and see your rewards grow over time.',
    body: [
      {
        type: 'paragraph',
        text: 'This development fixture uses a compact data table followed by supporting copy and a light promotional state.',
      },
      {
        type: 'chart',
        eyebrow: 'Demo trend',
        title: 'Example rewards earned over time',
        subtitle: 'A two-series line chart tests legends, plotted points and a highlighted label.',
        chartType: 'line',
        valuePrefix: '$',
        labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
        datasets: [
          {
            label: 'Standard plan',
            color: '#C9524A',
            values: [2.5, 4.1, 5.8, 7.2, 8.4, 10.1],
          },
          {
            label: 'Pro plan',
            color: '#171719',
            values: [3.2, 5.6, 8.1, 10.8, 13.4, 16.2],
          },
        ],
        highlightedLabel: 'Jun',
        source: 'Local development fixture — rewards shown are illustrative.',
      },
      { type: 'heading', text: 'Example rewards activity', level: 2 },
      {
        type: 'table',
        columns: ['Activity', 'Status', 'Example reward'],
        rows: [
          ['Eligible card purchase', 'Confirmed', '$1.20'],
          ['Partner offer', 'Pending', '$4.00'],
          ['Monthly balance reward', 'Confirmed', '$6.75'],
        ],
      },
      { type: 'heading', text: 'When rewards appear', level: 2 },
      {
        type: 'paragraph',
        text: 'Pending rewards become available after the qualifying activity is confirmed. Reversed or refunded purchases may not remain eligible.',
      },
      {
        type: 'promoCard',
        eyebrow: 'Local UI example',
        title: 'Track every reward in one place',
        body: 'Open the rewards screen to see confirmed, pending and redeemed activity.',
        tone: 'light',
        ctaLabel: 'See reward FAQs',
        ctaHref: '/article/faqs-about-rewards-and-payouts',
      },
    ],
  },
  {
    title: 'Making the most of partner perks',
    date: 'Dec 26, 2025',
    category: 'Rewards',
    excerpt: 'Unlock exclusive deals from our global network of partners.',
  },
  {
    title: 'Understanding your rewards rate and payouts',
    date: 'Dec 14, 2025',
    category: 'Rewards',
    excerpt: 'See how your rate is calculated and when rewards land in your account.',
  },
  {
    title: 'Boosting your rewards with a Pro plan',
    date: 'Dec 3, 2025',
    category: 'Rewards',
    excerpt: 'Compare plans and see how Pro increases what you earn.',
  },
  {
    title: 'Redeeming and tracking your rewards',
    date: 'Nov 21, 2025',
    category: 'Rewards',
    excerpt: 'Follow your earnings and put your rewards to work.',
  },
  {
    title: 'Referral rewards: invite friends and earn',
    date: 'Nov 9, 2025',
    category: 'Rewards',
    excerpt: 'Share nsave with friends and earn when they join.',
  },
  {
    title: 'FAQs about rewards and payouts',
    date: 'Oct 28, 2025',
    category: 'Rewards',
    excerpt: 'Answers to the most common questions about earning with nsave.',
  },

  // Security
  {
    title: 'Keeping your money safe: 2FA and device security',
    date: 'Jan 5, 2026',
    category: 'Security',
    excerpt: 'Simple steps to protect your account with two-factor authentication.',
    body: [
      {
        type: 'paragraph',
        text: 'A few small habits make it much harder for somebody else to access your account, even if they learn your password.',
      },
      { type: 'heading', text: 'Secure your sign-in', level: 2 },
      { type: 'heading', text: 'Use two-factor authentication', level: 3 },
      {
        type: 'paragraph',
        text: 'Keep your verification method private. nsave support will never ask you to read out a one-time code or approve an unfamiliar sign-in.',
      },
      { type: 'heading', text: 'Protect your recovery details', level: 3 },
      {
        type: 'list',
        items: [
          'Use a unique password for the email linked to your account.',
          'Keep your registered phone number up to date.',
          'Store backup codes somewhere separate from your phone.',
        ],
      },
      { type: 'heading', text: 'Review active devices', level: 2 },
      {
        type: 'paragraph',
        text: 'Remove old or unrecognised sessions immediately. If anything looks suspicious, freeze your cards and contact support from inside the app.',
      },
      {
        type: 'promoCard',
        eyebrow: 'Act quickly',
        title: 'Think someone accessed your account?',
        body: 'Secure your email, remove unknown devices and follow the compromised-account checklist.',
        tone: 'dark',
        ctaLabel: 'View the security checklist',
        ctaHref: '/article/what-to-do-if-your-account-is-compromised',
      },
    ],
  },
  {
    title: 'Spotting and avoiding common scams',
    date: 'Dec 24, 2025',
    category: 'Security',
    excerpt: 'Know the warning signs and learn how to keep your funds protected.',
    body: [
      {
        type: 'paragraph',
        text: 'Scammers often create urgency, impersonate a trusted organisation or ask you to move a conversation away from official support channels.',
      },
      { type: 'heading', text: 'Watch the video fixture', level: 2 },
      {
        type: 'video',
        src: 'https://player.vimeo.com/video/76979871',
        title: 'Local article video component example',
        caption:
          'External embed fixture used to test the responsive player, lazy loading and caption styling.',
      },
      { type: 'heading', text: 'Warning signs', level: 2 },
      {
        type: 'list',
        items: [
          'A message asks for a password, full card details or one-time security code.',
          'Someone pressures you to transfer immediately or keep the payment secret.',
          'A link uses a misspelled or unfamiliar domain.',
        ],
      },
      {
        type: 'promoCard',
        eyebrow: 'Stop and check',
        title: 'Never approve an action you did not start',
        body: 'Close the message or call, then contact support through the nsave app.',
        tone: 'red',
        ctaLabel: 'See compromised account steps',
        ctaHref: '/article/what-to-do-if-your-account-is-compromised',
      },
    ],
  },
  {
    title: 'How nsave protects your deposits',
    date: 'Dec 13, 2025',
    category: 'Security',
    excerpt: 'Where your money is held and the safeguards that keep it secure.',
  },
  {
    title: 'Managing trusted devices and sessions',
    date: 'Dec 1, 2025',
    category: 'Security',
    excerpt: 'Review the devices signed in to your account and remove any you don’t recognise.',
  },
  {
    title: 'Creating a strong passcode and biometrics',
    date: 'Nov 19, 2025',
    category: 'Security',
    excerpt: 'Lock down your app with a strong passcode and Face or Touch ID.',
  },
  {
    title: 'What to do if your account is compromised',
    date: 'Nov 7, 2025',
    category: 'Security',
    excerpt: 'Act fast to secure your account and get support if something’s wrong.',
  },
  {
    title: 'How we handle your personal data',
    date: 'Oct 26, 2025',
    category: 'Security',
    excerpt: 'A plain-English look at what we collect and how we keep it private.',
  },
];

/**
 * The Learn articles surfaced on the /help overview and their detail pages. Each
 * seed is expanded with a `slug` (derived from the title). Most fixtures use an
 * excerpt-only body; selected fixtures form a local article UI test set with
 * nested headings, media, tables, promo tones and interactive directories.
 * Full authored bodies are served from Payload.
 */
export const learnArticles: LearnArticle[] = learnArticleSeeds.map((seed) => {
  const slug = slugify(seed.title);
  return {
    ...seed,
    slug,
    urlPrefix: 'article',
    fullPath: `article/${slug}`,
    body: makeArticleBody(seed),
    author: fallbackAuthor,
  };
});
